import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tag, Check, Loader2, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from './CurrencySelector';
import { getPendingPromo, clearPendingPromo } from './PromoCatcher';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline voucher field for the checkout summary.
 * On apply: validates server-side via /api/vouchers/validate { code, subtotal }
 * Calls onApplied({ code, discount, type, value }) when valid.
 * Calls onCleared() when removed.
 */
export const VoucherField = ({ subtotal, applied, onApplied, onCleared }) => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);
  const { format } = useCurrency();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(!!applied);

  const apply = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    validateCode(code.trim().toUpperCase());
  };

  const validateCode = async (c) => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/vouchers/validate`,
        { code: c, subtotal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.valid) {
        toast.success(T(`Code appliqué : -${format(r.data.discount)}`, `Code applied: -${format(r.data.discount)}`));
        onApplied?.(r.data);
        setCode('');
        return true;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Code invalide', 'Invalid code'));
      if (err.response?.status === 404) clearPendingPromo();
    } finally {
      setLoading(false);
    }
    return false;
  };

  // Auto-apply a promo code captured from a shared ?promo= link
  useEffect(() => {
    const pending = getPendingPromo();
    if (!pending || applied) return;
    setOpen(true);
    setCode(pending);
    validateCode(pending).then((wasApplied) => {
      if (wasApplied) clearPendingPromo();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clear = () => {
    onCleared?.();
    setCode('');
  };

  if (applied) {
    return (
      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl p-3" data-testid="voucher-applied">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <Check className="w-4 h-4 text-green-700" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-green-900">{applied.code}</p>
            <p className="text-xs text-green-700 truncate">
              {applied.type === 'percent'
                ? `-${applied.value}% ${T('appliqué', 'applied')}`
                : `-${format(applied.value)} ${T('appliqué', 'applied')}`}
            </p>
            {applied.vendor_name && (
              <p className="text-[11px] text-green-700/80 truncate" data-testid="voucher-vendor-scope">
                {T('Produits', 'Products from')} {applied.vendor_name} {T('uniquement', 'only')}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={clear}
          className="text-green-700 hover:text-red-600 transition-colors p-1.5 rounded-lg hover:bg-white"
          aria-label="Remove voucher"
          data-testid="voucher-remove-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 text-sm font-medium text-[#0F4C5C] hover:text-[#E9C46A] transition-colors"
        data-testid="voucher-open-btn"
      >
        <Tag className="w-4 h-4" />
        {T('J\'ai un code promo', 'I have a promo code')}
      </button>
    );
  }

  return (
    <form onSubmit={apply} className="flex gap-2">
      <div className="relative flex-1">
        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0F4C5C]/50 pointer-events-none" />
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={T('Votre code', 'Your code')}
          className="pl-9 font-mono uppercase tracking-wider h-11"
          data-testid="voucher-input"
          autoFocus
        />
      </div>
      <Button type="submit" disabled={loading || !code.trim()} className="btn-primary h-11" data-testid="voucher-apply-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : T('Appliquer', 'Apply')}
      </Button>
    </form>
  );
};
